<script setup lang="ts">
  /**
   * Tooltip 统一组件
   * @description
   * 1. 封装了 TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent
   * 2. 提供简化的 API，通过 props 控制内容和配置
   * 3. 避免外部引入过多子组件
   */
  import {
    TooltipProvider,
    TooltipRoot,
    TooltipTrigger,
    type TooltipContentProps,
    type TooltipRootEmits,
    type TooltipRootProps,
    useForwardPropsEmits,
  } from 'reka-ui'
  import { reactiveOmit } from '@vueuse/core'
  import TooltipContent from './TooltipContent.vue'
  import { HTMLAttributes } from 'vue'

  interface Props extends TooltipRootProps {
    /**
     * Tooltip 显示的内容
     */
    content?: string
    /**
     * Tooltip 显示的位置
     */
    side?: TooltipContentProps['side']
    /**
     * Tooltip 对齐方式
     */
    align?: TooltipContentProps['align']
    /**
     * 侧边偏移量
     */
    sideOffset?: number
    /**
     * 相对于“start”或“end”选项的像素偏移量。
     */
    alignOffset?: number
    /**
     * Tooltip 碰撞检测边界。
     */
    collisionBoundary?: TooltipContentProps['collisionBoundary']
    /**
     * Tooltip 与碰撞边界之间的距离。
     */
    collisionPadding?: TooltipContentProps['collisionPadding']
    /**
     * 当触发元素完全离开碰撞边界时隐藏 Tooltip。
     */
    hideWhenDetached?: TooltipContentProps['hideWhenDetached']
    /**
     * 延迟显示时间，单位毫秒
     */
    delayDuration?: number
    /**
     * Tooltip 是否禁用
     */
    disabled?: boolean

    contentClass?: HTMLAttributes['class']
  }

  const props = withDefaults(defineProps<Props>(), {
    side: 'top',
    align: 'center',
    sideOffset: 4,
    alignOffset: 0,
    delayDuration: 200,
    disabled: false,
  })

  const emits = defineEmits<TooltipRootEmits>()

  const delegatedProps = reactiveOmit(
    props,
    'content',
    'side',
    'align',
    'sideOffset',
    'alignOffset',
    'collisionBoundary',
    'collisionPadding',
    'hideWhenDetached',
    'disabled',
    'delayDuration',
  )

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <TooltipProvider :delay-duration="delayDuration">
    <TooltipRoot v-bind="forwarded">
      <TooltipTrigger as-child>
        <slot />
      </TooltipTrigger>
      <TooltipContent
        v-if="!disabled && (content || $slots.content)"
        :side="side"
        :align="align"
        :side-offset="sideOffset"
        :align-offset="alignOffset"
        :collision-boundary="collisionBoundary"
        :collision-padding="collisionPadding"
        :hide-when-detached="hideWhenDetached"
        :class="contentClass"
      >
        <div
          v-if="content"
          class="text-s-medium text-white-black p-1 bg-floating-tooltip-container rounded-m"
        >
          {{ content }}
        </div>
        <slot name="content"></slot>
      </TooltipContent>
    </TooltipRoot>
  </TooltipProvider>
</template>
